/**
 * Crowdin CDN distribution (manifest + URL helpers).
 * @see https://support.crowdin.com/cdn-distributions/
 */

/** Dev / local Crowdin CDN distribution (fallback when no override applies). */
export const DEFAULT_CROWDIN_DISTRIBUTION_HASH = 'e-49f60b16312426db198e3b3k50';

/** Non-prod MoneyGram UI hosts: `{env}quickstage.moneygram.com` (see BFF `getUiVersionUrl`). */
export const QUICKSTAGE_MONEYGRAM_HOST_SUFFIX = 'quickstage.moneygram.com';

/** Production UI host (no env prefix). */
export const QUICKSTAGE_MONEYGRAM_PROD_HOST = 'quickstage.moneygram.com';

const HASH_DEV = 'e-49f60b16312426db198e3b3k50';
const HASH_QA = 'e-f8e421be861f76bee8a33b3k50';
const HASH_Q1 = 'e-106599e95d94316d0dd63b3k50';
const HASH_EXT= 'e-a23b4a353725a2cec1f63b3k50';
const HASH_PROD = 'e-3247f5ba3fde6f6423753b3k50';

/**
 * Crowdin distribution hash by **GitLab deploy `ENVIRONMENT`** (hostname-derived or bootstrap
 * `crowdinDeployEnvironment`). One web artifact can use this map at runtime instead of baking
 * `VITE_ENVIRONMENT` per bucket.
 *
 * `CROWDIN_DISTRIBUTION_HASH_PRODUCTION` is commented out for now; when prod uses its own Crowdin
 * vault (distinct from Q1), restore that export and point `prod` below at it.
 */
// export const CROWDIN_DISTRIBUTION_HASH_PRODUCTION = HASH_Q1;

export const CROWDIN_DISTRIBUTION_HASH_BY_DEPLOY_ENV: Readonly<Record<string, string>> = {
  d2: HASH_DEV,
  d3: HASH_DEV,
  d5: HASH_DEV,
  q2: HASH_QA,
  q3: HASH_QA,
  q5: HASH_QA,
  q1: HASH_Q1,
  ext: HASH_EXT,
  prod: HASH_PROD,
};

/**
 * Crowdin distribution hash by deploy environment (`VITE_ENVIRONMENT`, lowercase).
 * Set in CI via `ENVIRONMENT` → `turbo-build-apps.sh` → `VITE_ENVIRONMENT`.
 * Unknown values fall back to {@link DEFAULT_CROWDIN_DISTRIBUTION_HASH}; use
 * `VITE_CROWDIN_DISTRIBUTION_HASH` or `/config/i18n.json` for other targets.
 */
export const CROWDIN_DISTRIBUTION_HASH_BY_ENV: Readonly<Record<string, string>> = {
  dev: HASH_DEV,
  development: HASH_DEV,
  local: HASH_DEV,
  qa: HASH_QA,
  q1: HASH_Q1,
};

/** Same-origin static bootstrap (Vite: `apps/web/public/config/i18n.json`). */
export const CROWDIN_I18N_BOOTSTRAP_PATH = '/config/i18n.json';

export type CrowdinI18nBootstrapConfig = {
  /** Crowdin CDN distribution segment, e.g. `e-49f60b16312426db198e3b3k50` */
  crowdinDistributionHash?: string;
  /**
   * Optional GitLab-style deploy key (`d5`, `q5`, `q1`, `ext`, `prod`, …) when the host does not
   * match MoneyGram Quickstage patterns or you need to force a tier (e.g. smoke tests on a custom domain).
   */
  crowdinDeployEnvironment?: string;
};

export type CrowdinDistributionManifest = {
  files: string[];
  languages: string[];
  language_mapping?: unknown[];
  custom_languages?: unknown[];
  timestamp: number;
  content: Record<string, string[]>;
  mapping?: string[];
};

/** Last-known paths when manifest cannot be fetched (keep in sync with Crowdin bundle). */
export const FALLBACK_CROWDIN_CONTENT_PATHS: Record<string, string> = {
  es: '/content/es/es-US.json',
  'es-US': '/content/es/es-US.json',
  'es-PA': '/content/es-PA/es-PA.json',
};

export function crowdinCdnBase(distributionHash: string): string {
  return `https://distributions.crowdin.net/${distributionHash}`;
}

/** Vite build-time explicit override only (optional). */
export function getCrowdinDistributionHashFromEnv(): string | undefined {
  try {
    if (typeof import.meta !== 'undefined') {
      const env = (import.meta as { env?: { VITE_CROWDIN_DISTRIBUTION_HASH?: string } }).env
        ?.VITE_CROWDIN_DISTRIBUTION_HASH;
      if (env && typeof env === 'string' && env.trim() !== '') return env.trim();
    }
  } catch {
    /* non-Vite / older runtimes */
  }
  return undefined;
}

export function isPlausibleCrowdinDistributionHash(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Za-z0-9_-]+$/.test(value) && value.length >= 8;
}

/**
 * Parses `{env}quickstage.moneygram.com` and `quickstage.moneygram.com` → `prod`.
 * Returns a key for {@link CROWDIN_DISTRIBUTION_HASH_BY_DEPLOY_ENV}, or `undefined` if unknown.
 */
export function inferCrowdinDeployEnvironmentFromHostname(hostname: string): string | undefined {
  const h = hostname.trim().toLowerCase();
  if (!h) return undefined;
  if (h === QUICKSTAGE_MONEYGRAM_PROD_HOST) return 'prod';
  const suffix = QUICKSTAGE_MONEYGRAM_HOST_SUFFIX;
  if (!h.endsWith(suffix)) return undefined;
  const prefix = h.slice(0, -suffix.length);
  if (!prefix || !/^[a-z0-9]+$/.test(prefix)) return undefined;
  return prefix;
}

/** Hash for a deploy-environment key (`d5`, `q1`, `prod`, …). */
export function getCrowdinDistributionHashForDeployEnvironment(
  deployEnvironment: string | undefined
): string | undefined {
  if (!deployEnvironment || typeof deployEnvironment !== 'string') return undefined;
  const key = deployEnvironment.trim().toLowerCase();
  if (!key) return undefined;
  const mapped = CROWDIN_DISTRIBUTION_HASH_BY_DEPLOY_ENV[key];
  return mapped && isPlausibleCrowdinDistributionHash(mapped) ? mapped : undefined;
}

/** Browser only: hostname → {@link CROWDIN_DISTRIBUTION_HASH_BY_DEPLOY_ENV}. */
export function getCrowdinDistributionHashFromDeployHostname(): string | undefined {
  if (typeof window === 'undefined' || !window.location?.hostname) return undefined;
  const tier = inferCrowdinDeployEnvironmentFromHostname(window.location.hostname);
  return getCrowdinDistributionHashForDeployEnvironment(tier);
}

/** Hash from `VITE_ENVIRONMENT` when it matches {@link CROWDIN_DISTRIBUTION_HASH_BY_ENV}. */
export function getCrowdinDistributionHashForViteEnvironment(): string | undefined {
  try {
    if (typeof import.meta === 'undefined') return undefined;
    const raw = (import.meta as { env?: { VITE_ENVIRONMENT?: string } }).env?.VITE_ENVIRONMENT;
    if (!raw || typeof raw !== 'string') return undefined;
    const key = raw.trim().toLowerCase();
    if (!key) return undefined;
    const mapped = CROWDIN_DISTRIBUTION_HASH_BY_ENV[key];
    return mapped && isPlausibleCrowdinDistributionHash(mapped) ? mapped : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Synchronous hash: explicit Vite hash → **deploy host map** → `VITE_ENVIRONMENT` map → default.
 * Non-browser skips hostname; browser should prefer {@link resolveCrowdinDistributionHash} (bootstrap may set a hash).
 */
export function getCrowdinDistributionHash(): string {
  return (
    getCrowdinDistributionHashFromEnv() ??
    getCrowdinDistributionHashFromDeployHostname() ??
    getCrowdinDistributionHashForViteEnvironment() ??
    DEFAULT_CROWDIN_DISTRIBUTION_HASH
  );
}

function hashFromBootstrapConfig(json: CrowdinI18nBootstrapConfig | undefined): string | undefined {
  const direct = json?.crowdinDistributionHash;
  if (isPlausibleCrowdinDistributionHash(direct)) return direct;
  const fromDeploy = getCrowdinDistributionHashForDeployEnvironment(json?.crowdinDeployEnvironment);
  if (fromDeploy) return fromDeploy;
  return undefined;
}

/**
 * Browser: **`VITE_CROWDIN_DISTRIBUTION_HASH`** (if set) → **`GET` bootstrap** (`crowdinDistributionHash` or
 * `crowdinDeployEnvironment`) → **hostname → {@link CROWDIN_DISTRIBUTION_HASH_BY_DEPLOY_ENV}** →
 * **`VITE_ENVIRONMENT` map** → **default**.
 * Non-browser: same as {@link getCrowdinDistributionHash} (no fetch, no hostname).
 */
export async function resolveCrowdinDistributionHash(): Promise<string> {
  const fromEnv = getCrowdinDistributionHashFromEnv();
  if (fromEnv) return fromEnv;

  if (typeof window === 'undefined') {
    return getCrowdinDistributionHash();
  }

  try {
    const res = await fetch(CROWDIN_I18N_BOOTSTRAP_PATH, {
      credentials: 'same-origin',
      cache: 'no-cache',
    });
    if (!res.ok) {
      console.warn(
        `[i18n] Crowdin bootstrap HTTP ${res.status} for ${CROWDIN_I18N_BOOTSTRAP_PATH}; using default hash`
      );
      return getCrowdinDistributionHash();
    }
    const json = (await res.json()) as CrowdinI18nBootstrapConfig;
    const fromBootstrap = hashFromBootstrapConfig(json);
    if (fromBootstrap) {
      return fromBootstrap;
    }
    const rawHash = json?.crowdinDistributionHash;
    if (rawHash != null && String(rawHash).trim() !== '' && !isPlausibleCrowdinDistributionHash(rawHash)) {
      console.warn('[i18n] Crowdin bootstrap crowdinDistributionHash is present but invalid');
    }
    const rawDeploy = json?.crowdinDeployEnvironment;
    if (rawDeploy != null && String(rawDeploy).trim() !== '') {
      const k = String(rawDeploy).trim().toLowerCase();
      if (!getCrowdinDistributionHashForDeployEnvironment(k)) {
        console.warn(`[i18n] Crowdin bootstrap crowdinDeployEnvironment not recognized: ${k}`);
      }
    }
  } catch (e) {
    console.warn(`[i18n] Crowdin bootstrap fetch failed; using default hash`, e);
  }
  return getCrowdinDistributionHash();
}

export async function fetchCrowdinDistributionManifest(
  distributionHash: string
): Promise<{ pathByLanguage: Record<string, string>; timestamp: number }> {
  const manifestUrl = `${crowdinCdnBase(distributionHash)}/manifest.json`;
  const res = await fetch(manifestUrl);
  if (!res.ok) {
    throw new Error(`Crowdin manifest HTTP ${res.status}: ${manifestUrl}`);
  }
  const m = (await res.json()) as CrowdinDistributionManifest;
  const pathByLanguage: Record<string, string> = {};
  for (const [lang, paths] of Object.entries(m.content ?? {})) {
    if (Array.isArray(paths) && paths[0]) pathByLanguage[lang] = paths[0];
  }
  return { pathByLanguage, timestamp: Number(m.timestamp) || 0 };
}

export function buildCrowdinTranslationFileUrl(
  distributionHash: string,
  contentPath: string,
  timestamp: number
): string {
  const base = crowdinCdnBase(distributionHash);
  const qs = timestamp ? `?v=${encodeURIComponent(String(timestamp))}` : '';
  return `${base}${contentPath}${qs}`;
}
